import { ElementType } from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
    SmsOutlined,
    EmailOutlined,
    WalletOutlined,
    DesktopWindowsOutlined,
    PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useLanguage } from '../../../../context/LanguageContext';
import type { ChannelPaletteKey } from '../../../../theme';

interface ChannelConfig {
    labelKey?: string;
    icon: ElementType;
    // Clé de la palette `channel` du thème ; absente = canal inconnu (couleur principale)
    paletteKey?: ChannelPaletteKey;
}

const CHANNEL_KEY = 'pages.clients.queryBuilder.countCards.channels';

const CHANNELS: Record<string, ChannelConfig> = {
    sms: { labelKey: `${CHANNEL_KEY}.sms`, icon: SmsOutlined, paletteKey: 'sms' },
    email: { labelKey: `${CHANNEL_KEY}.email`, icon: EmailOutlined, paletteKey: 'email' },
    wallet: { labelKey: `${CHANNEL_KEY}.wallet`, icon: WalletOutlined, paletteKey: 'wallet' },
    cashdesk: { labelKey: `${CHANNEL_KEY}.cashDesk`, icon: DesktopWindowsOutlined, paletteKey: 'cashdesk' },
    courrier: { labelKey: `${CHANNEL_KEY}.courrier`, icon: DesktopWindowsOutlined, paletteKey: 'courrier' },
};

const UNKNOWN_CHANNEL: ChannelConfig = { icon: PeopleAltIcon };

const ALWAYS_VISIBLE_CHANNELS = ['wallet'];

const normalizeChannel = (channel: string) => channel.trim().toLowerCase();

interface CountCardsProps {
    listCount: Array<{ channel: string; count: number }>;
    total?: number;
}

function CountCards({ listCount, total }: CountCardsProps) {
    const theme = useTheme();
    const { t, formatLocale } = useLanguage();

    const items = [
        ...listCount,
        ...ALWAYS_VISIBLE_CHANNELS
            .filter(channel => !listCount.some(item => normalizeChannel(item.channel) === channel))
            .map(channel => ({ channel, count: 0 })),
    ];
    const base = total ?? items.reduce((acc, item) => acc + item.count, 0);

    return (
        <Grid container spacing={2}>
            {items.map(item => {
                const { labelKey, icon: Icon, paletteKey } = CHANNELS[normalizeChannel(item.channel)] ?? UNKNOWN_CHANNEL;
                const color = paletteKey ? theme.palette.channel[paletteKey] : theme.palette.primary.main;
                const percentage = base > 0 ? Math.min(100, Math.round((item.count / base) * 100)) : 0;

                return (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.channel}>
                        <Card elevation={0} sx={{ border: `2px solid ${color}`, borderRadius: 2, height: '120px', overflow: 'hidden' }}>
                            <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ p: 0.8, borderRadius: 1.5, backgroundColor: alpha(color, 0.09), display: 'flex' }}>
                                        <Icon sx={{ fontSize: 20, color }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                            {labelKey ? t(labelKey) : item.channel}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: '0.7rem', display: 'block' }}>
                                            {t('pages.clients.queryBuilder.countCards.percentOfTotal', { percentage })}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color, fontSize: '1.8rem', lineHeight: 1 }}>
                                    {item.count.toLocaleString(formatLocale)}
                                    <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5, fontSize: '0.7rem' }}>
                                        {t('pages.clients.queryBuilder.countCards.clientsLabel')}
                                    </Typography>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
}

export default CountCards;
